import * as React from 'react';
import {
  Box,
  StackLayout,
  StatusBanner,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  mapBulkActionSessionToBannerViewModel,
  mapBulkActionSessionToProgressViewModel,
  mapBulkActionSessionToTitleViewModel,
  useGetEntityTypeName,
  type BulkActionBannerViewModel,
  type BulkActionSession,
} from '@web-config-app/core-react';
import type { BulkActionOperationType } from '@web-config-app/core';
import type {
  StatusReportTableRowStatusTextMap,
  StatusSummaryFilterStatus,
} from '@web-config-app/core-react-ui';
import {
  BulkActionReportingModal,
  BulkActionReportingProgressBar,
  StatusReportTable,
  createDefaultStatusReportColumns,
} from '@web-config-app/core-react-ui';
import { BulkActionReportingModalFooter } from './bulk-action-reporting-modal-footer.component';
import { useMapSessionItemsToRows } from './use-map-session-items-to-rows';

export type { BulkActionSession };

export interface BulkActionReportingModalContainerProps {
  open: boolean;
  session: BulkActionSession;
  onOpenChange: (open: boolean) => void;
  onCancelCTA?: () => void;
  onRetryCTA?: () => void;
  onDoneCTA?: () => void;
}

const BulkActionBanner = ({
  bannerModel,
  entityLabel,
}: {
  bannerModel: BulkActionBannerViewModel | undefined;
  entityLabel: string;
}) => {
  const { formatMessage } = useIntl();

  if (!bannerModel) {
    return null;
  }

  const bannerTitle = formatMessage(
    { id: bannerModel.titleMessageId },
    { entityType: entityLabel },
  );

  const bannerMessage = bannerModel.messageMessageId
    ? formatMessage(
        { id: bannerModel.messageMessageId },
        {
          entityType: entityLabel,
          operation: bannerModel.messageOperationMessageId
            ? formatMessage({ id: bannerModel.messageOperationMessageId })
            : undefined,
          operationPastTense: bannerModel.messageOperationPastTenseMessageId
            ? formatMessage({
                id: bannerModel.messageOperationPastTenseMessageId,
              })
            : undefined,
        },
      )
    : undefined;

  return (
    <StatusBanner status={bannerModel.status} title={bannerTitle}>
      {bannerMessage && <UtilityText as="p">{bannerMessage}</UtilityText>}
    </StatusBanner>
  );
};

const sharedStatusText = {
  skipped: 'BULK_ACTION_REPORT_STATUS_SKIPPED',
  error: 'BULK_ACTION_REPORT_STATUS_FAILED',
} as const;

const statusTextMapByOperationType: Record<
  BulkActionOperationType,
  StatusReportTableRowStatusTextMap
> = {
  import: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_IMPORTED',
    loading: 'BULK_ACTION_REPORT_STATUS_IMPORTING',
  },
  export: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_EXPORTED',
    loading: 'BULK_ACTION_REPORT_STATUS_EXPORTING',
  },
  clone: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_CREATED',
    loading: 'BULK_ACTION_REPORT_STATUS_DUPLICATING',
  },
  copyToEnvironment: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_COPIED',
    loading: 'BULK_ACTION_REPORT_STATUS_COPYING',
  },
  publish: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_PUBLISHED',
    loading: 'BULK_ACTION_REPORT_STATUS_PUBLISHING',
  },
  archive: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_ARCHIVED',
    loading: 'BULK_ACTION_REPORT_STATUS_ARCHIVING',
  },
  delete: {
    ...sharedStatusText,
    success: 'BULK_ACTION_REPORT_STATUS_DELETED',
    loading: 'BULK_ACTION_REPORT_STATUS_DELETING',
  },
};

const EmptyStateFilter = () => {
  const { formatMessage } = useIntl();
  return (
    <StackLayout horizontalAlignment="center" spacing="$quarter">
      <UtilityText emphasis="emphasized">
        {formatMessage({ id: 'NO_FILTER_RESULTS_FOUND' })}
      </UtilityText>
      <UtilityText emphasis="subtle">
        {formatMessage({ id: 'NO_FILTER_RESULTS_FOUND_DESCRIPTION' })}
      </UtilityText>
    </StackLayout>
  );
};

export const BulkActionReportingModalContainer = ({
  open,
  session,
  onOpenChange,
  onCancelCTA,
  onRetryCTA,
  onDoneCTA,
}: BulkActionReportingModalContainerProps) => {
  const { formatMessage } = useIntl();
  const getEntityTypeName = useGetEntityTypeName();
  const { operationType } = session;
  const displayedEntityCount = session.stats.total;

  const [activeStatusFilter, setActiveStatusFilter] =
    React.useState<StatusSummaryFilterStatus | null>(null);

  // Clear the filter when the modal closes so it never persists into the next open.
  React.useEffect(() => {
    if (!open) {
      setActiveStatusFilter(null);
    }
  }, [open]);

  const resolvedEntityLabel = getEntityTypeName(session.domainEntityType, 1);
  const resolvedEntityLabelPlural = getEntityTypeName(
    session.domainEntityType,
    displayedEntityCount,
  );
  const titleModel = mapBulkActionSessionToTitleViewModel(session);
  const title = formatMessage(
    { id: titleModel.titleMessageId },
    {
      targetEnvironment: session.metadata?.targetEnvironmentName,
      operation: formatMessage({ id: titleModel.operationMessageId }),
      entityType: resolvedEntityLabelPlural,
    },
  );
  const progressModel = mapBulkActionSessionToProgressViewModel(session);
  const bannerModel = mapBulkActionSessionToBannerViewModel(session);
  const allTableItems = useMapSessionItemsToRows(session);

  const filteredTableItems = React.useMemo(() => {
    if (!activeStatusFilter) {
      return allTableItems;
    }
    return allTableItems.filter((item) => item.status === activeStatusFilter);
  }, [activeStatusFilter, allTableItems]);

  const handleDone = () => {
    onDoneCTA?.();
    onOpenChange(false);
  };

  // Reset the active filter on retry
  const handleRetry = onRetryCTA
    ? () => {
        setActiveStatusFilter(null);
        onRetryCTA();
      }
    : undefined;

  const showEmptyStateFilter = filteredTableItems.length === 0;

  return (
    <BulkActionReportingModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      header={
        operationType === 'import' || operationType === 'export' ? (
          <BulkActionReportingProgressBar
            operationType={operationType}
            status={progressModel.status}
            mode={progressModel.mode}
            processedCount={progressModel.processedCount}
            totalCount={progressModel.totalCount}
            counts={progressModel.counts}
            entityLabel={resolvedEntityLabel}
            entityLabelPlural={resolvedEntityLabelPlural}
            onStatusFilterChange={setActiveStatusFilter}
            activeStatusFilter={activeStatusFilter}
          />
        ) : (
          <BulkActionReportingProgressBar
            operationType={operationType}
            status={progressModel.status}
            mode={progressModel.mode}
            processedCount={progressModel.processedCount}
            totalCount={progressModel.totalCount}
            counts={progressModel.counts}
            onStatusFilterChange={setActiveStatusFilter}
            activeStatusFilter={activeStatusFilter}
          />
        )
      }
      footer={
        <BulkActionReportingModalFooter
          operationType={operationType}
          controllerStatus={session.status}
          hasFailures={session.stats.failed > 0}
          onCancel={onCancelCTA}
          onRetry={handleRetry}
          onDone={handleDone}
          targetEnvironment={session.targetEnvironment}
        />
      }
    >
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'minmax(0, 1fr) auto',
          gap: '$one',
        }}
      >
        {showEmptyStateFilter ? (
          <EmptyStateFilter />
        ) : (
          <StatusReportTable
            caption={title}
            items={filteredTableItems}
            columns={createDefaultStatusReportColumns()}
            statusTextMap={statusTextMapByOperationType[operationType]}
            ariaLabel={title}
          />
        )}
        <BulkActionBanner
          bannerModel={bannerModel}
          entityLabel={resolvedEntityLabelPlural}
        />
      </Box>
    </BulkActionReportingModal>
  );
};
